import React, { useEffect, useState } from "react";
import { useLocalStore, useObserver } from "mobx-react";
import {
  AccommodationCardDetailsFragment,
  AccommodationDocumentType,
  AccommodationEventLog_GetEvents,
  GetAccommodationFiles,
  AccommodationSearch_Search,
  useAccommodation_DeleteMutation,
  useUploadAccommodationFileMutation,
  useAccommodationUpdateUnfitMutation
} from "@graphql";
import locale from "@bonliva-crm/locale";
import { AccommodationEditorLocation, useNavigation } from "@navigation";
import { ErrorCard, SpinnerOverlay, Topbar } from "@components";
import { Tabs } from "@components/Tabs";
// import Highlighter from "@components/Highlighter"; TODO add in search task
import { AccommodationProfileStore } from "./AccommodationProfileStore";
import AccommodationProfileLayout from "./AccommodationProfileLayout";
import { ErrorBoundary, withErrorBoundary } from "@components/error-boundary";
import { BasicProfileLayout } from "@containers/BasicProfileLayout";
import { AccommodationEventLogStore } from "@containers/EventLog/AccommodationEventLog/AccommodationEventLogStore";
import AccommodationEventLog from "@containers/EventLog/AccommodationEventLog/AccommodationEventLog";
import { DocumentUploader } from "@containers/DocumentUploader/DocumentUploader";
import { DnDLocalContainer } from "@containers/DnDContainer";
import { useConfirmDialog, useSnackbars } from "@hooks";
import { store as storeBase } from "@store";
import { UnfitItemComponent } from "@common/UnfitItemComponent";
import { OkCancelDialogOptions } from "@containers";
import { AccommodationCard } from "../AccommodationCard/AccommodationCard";
import { AccommodationDocuments } from "./AccommodationDocuments/AccommodationDocuments";

type Props = {
  customerGroup?: string;
  id: string;
  directUri: string;
};

const AccommodationProfile: React.FC<Props> = ({ id }) => {
  let store = useLocalStore(() => new AccommodationProfileStore(id));
  let eventStore = useLocalStore(() => new AccommodationEventLogStore(id));
  const navigation = useNavigation();

  const [files, setFiles] = useState<File[]>([]);
  const { showSuccess, showError } = useSnackbars();

  const [uploadDocs] = useUploadAccommodationFileMutation();
  const [updateUnfit] = useAccommodationUpdateUnfitMutation();
  const [deleteAccommodation] = useAccommodation_DeleteMutation({
    refetchQueries: [AccommodationSearch_Search.OperationName]
  });
  const { confirm } = useConfirmDialog();

  useEffect(() => {
    store = new AccommodationProfileStore(id);
    eventStore = new AccommodationEventLogStore(id);
  }, [id]);

  const handleUpload = async (documentType, files) => {
    for (const file of files) {
      await uploadDocs({
        variables: {
          id,
          file,
          documentType
        },
        refetchQueries: [
          AccommodationEventLog_GetEvents.OperationName,
          GetAccommodationFiles.OperationName
        ]
      }).then(() => showSuccess(`Uploaded`));
    }
  };

  const handleDelete = async () => {
    confirm(new OkCancelDialogOptions()).then((res) => {
      if (res === "ok") {
        deleteAccommodation({ variables: { id } })
          .then(() => {
            showSuccess(locale.Deleted);
            navigation.close();
          })
          .catch((err) =>
            showError(`Cannot delete Accommodation\nReason: ${err.toString()}`)
          );
      }
    });
  };

  const renderTabs = () => {
    const { tab, handleTabChange } = store;

    const request = () => {
      storeBase()
        .upload.requestFileUpload({ multiple: true })
        .then((files) => setFiles(files));
    };

    return (
      <Tabs
        tabs={[
          {
            label: locale.ActionLog,
            value: "events",
            content: (
              <ErrorBoundary>
                <AccommodationEventLog store={eventStore} />
              </ErrorBoundary>
            )
          },
          {
            fitted: false,
            label: locale.Files,
            value: "files",
            content: (
              <ErrorBoundary>
                <AccommodationDocuments id={id} onUpload={request} />
              </ErrorBoundary>
            )
          }
        ]}
        value={tab}
        onChange={(e, v) => handleTabChange(v)}
        withHref
        TabsProps={{ indicatorColor: "primary" }}
        AppBarProps={{ style: { borderTopRightRadius: 4, borderTopLeftRadius: 4 } }}
      />
    );
  };

  return useObserver(() => {
    if (store.loading) {
      return <SpinnerOverlay transparent show />;
    }

    if (store.error) {
      return (
        <BasicProfileLayout>
          <ErrorCard
            centered
            message={store.error}
            title={locale.ErrorOccurred}
            style={{ marginTop: 120 }}
          />
        </BasicProfileLayout>
      );
    }

    return (
      <DnDLocalContainer multiple onDrop={(f) => setFiles(f)} noClick>
        <DocumentUploader
          documentTypes={Object.values(AccommodationDocumentType).toLabelValue(
            (v) => locale.accommodationDocumentType[v]
          )}
          files={files}
          onUpload={(docType) => handleUpload(docType, files)}
          onClose={() => setFiles([])}
        />
        <AccommodationProfileLayout
          id={id}
          topbar={<Topbar />}
          rightBar={
            <UnfitItemComponent
              name={store.data.unfit?.userRef.Name}
              value={store.data.unfit?.value}
              comment={store.data.unfit?.comment}
              save={(unfit, comment) => {
                return updateUnfit({
                  variables: {
                    id,
                    value: unfit,
                    comment
                  }
                }).then((result) => {
                  store.data.unfit = result.data.updateUnfitAccommodation;
                });
              }}
            />
          }
          main={
            <>
              <ErrorBoundary>
                <AccommodationCard
                  data={store.data as AccommodationCardDetailsFragment}
                  fullInfo
                  onEdit={() =>
                    navigation.navigate(new AccommodationEditorLocation(store.id))
                  }
                  onDelete={handleDelete}
                />
              </ErrorBoundary>

              {withErrorBoundary(renderTabs())}
            </>
          }
        />
      </DnDLocalContainer>
    );
  });
};

export default AccommodationProfile;
